import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MoreHorizontal,
  Plus,
  EyeOff,
  X,
  Globe,
  ExternalLink,
  MapPin,
  Tags,
  Star,
  AlertTriangle,
  GripVertical,
  Check,
  Edit2,
} from 'lucide-react';

type ProspectStatus = 'new' | 'to_visit' | 'visited_interested' | 'visited_not_interested' | 'nurture' | 'disqualified' | null;

export type Contact = {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  source: string | null;
  last_activity_at: string | null;
  tags: string[] | null;
  created_at?: string;
  lifecycle_stage?: 'contact' | 'lead' | 'client' | null;
  vat_number?: string | null;
  client_rank?: number | null;
  company?: string | null;
  role?: string | null;
  preferred_language?: string | null;
  readonly?: boolean;
  company_address?: string | null;
  company_city?: string | null;
  company_zip?: string | null;
  google_maps_url?: string | null;
  website?: string | null;
  prospect_status?: ProspectStatus;
  lat?: number | null;
  lng?: number | null;
  interest_level?: 'low' | 'medium' | 'high' | null;
  route_index?: number | null;
  latest_note?: string | null;
  latest_note_id?: string | null;
};

type ColumnDefinition = {
  key: string;
  label: string;
  accessorKey?: string;
  sortable?: boolean;
};

type ContactsDataTableProps = {
  data: Contact[];
  columns: ColumnDefinition[];
  columnOrder: string[];
  visibleColumns: Record<string, boolean>;
  onColumnOrderChange: (order: string[]) => void;
  onVisibleColumnsChange: (visible: Record<string, boolean>) => void;
  onSortingChange?: (sorting: SortingState) => void;
  onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
  initialSorting?: SortingState;
  initialColumnFilters?: ColumnFiltersState;
  onUpdateContact?: (contactId: string, field: string, value: any, additionalFields?: Record<string, any>) => Promise<void>;
  onEditContact?: (contact: Contact) => Promise<void>;
  onDeleteContact?: (contact: Contact) => void;
  formatRelative: (iso?: string | null) => string;
  stageOptions: Array<{ value: string; label: string }>;
  prospectStatusOptions: Array<{ value: string; label: string }>;
  leadStatusOptions: Array<{ value: string; label: string }>;
  leadStages: Record<string, 'new' | 'qualified' | 'scheduled' | 'won' | 'lost'>;
  duplicateEmails: Set<string>;
  duplicatePhones: Set<string>;
  leadCounts: Record<string, number>;
  canEdit: boolean;
  t: (key: string, options?: any) => string;
  currentPage?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  onFilteredCountChange?: (count: number) => void;
};

// Sortable Header Component
function SortableHeader({ 
  column, 
  children, 
  onMenuClick,
  menuOpen,
  menuRef,
}: {
  column: any;
  children: React.ReactNode;
  onMenuClick: (e: React.MouseEvent) => void;
  menuOpen: boolean;
  menuRef?: React.RefObject<HTMLDivElement>;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sortDirection = column.getIsSorted();

  return (
    <th
      ref={setNodeRef}
      style={style}
      className="px-3 py-2 text-gray-700 dark:text-gray-300 font-semibold text-xs uppercase tracking-wider bg-gray-50 dark:bg-gray-700/50 sticky top-0 z-10 select-none"
    >
      <div className="flex items-center gap-2 relative">
        {/* Drag handle - separate from sort click area */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-move hover:text-gray-900 dark:hover:text-gray-100 opacity-50 hover:opacity-100 transition-opacity p-1"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </div>
        
        {/* Sort click area - separate from drag handle */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (column.getCanSort()) {
              column.toggleSorting(column.getIsSorted() === 'asc');
            }
          }}
          className="flex items-center gap-1 flex-1 cursor-pointer hover:text-gray-900 dark:hover:text-gray-100"
        >
          {children}
          {sortDirection === 'asc' ? (
            <ArrowUp className="w-3 h-3" />
          ) : sortDirection === 'desc' ? (
            <ArrowDown className="w-3 h-3" />
          ) : (
            <ArrowUpDown className="w-3 h-3 opacity-30" />
          )}
        </div>
        
        <div 
          className="relative" 
          ref={menuRef}
        >
          <button
            onClick={onMenuClick}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 opacity-60 hover:opacity-100 transition-opacity"
          >
            <MoreHorizontal className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </th>
  );
}

export function ContactsDataTable({
  data,
  columns,
  columnOrder,
  visibleColumns,
  onColumnOrderChange,
  onVisibleColumnsChange,
  onSortingChange,
  onColumnFiltersChange,
  initialSorting = [],
  initialColumnFilters = [],
  onUpdateContact,
  onEditContact,
  onDeleteContact,
  formatRelative,
  stageOptions,
  prospectStatusOptions,
  leadStatusOptions,
  leadStages,
  duplicateEmails,
  duplicatePhones,
  leadCounts,
  canEdit,
  t,
  currentPage = 1,
  itemsPerPage = 10,
  onPageChange,
  onFilteredCountChange,
}: ContactsDataTableProps) {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters);

  // Track previous initial values to detect external changes
  const prevInitialSortingRef = React.useRef<string>(JSON.stringify(initialSorting));
  const prevInitialFiltersRef = React.useRef<string>(JSON.stringify(initialColumnFilters));
  const lastSortingUpdateRef = React.useRef<string>('');
  const lastFiltersUpdateRef = React.useRef<string>('');

  // Update state when initial values change (from database) - only if truly different
  useEffect(() => {
    const initialStr = JSON.stringify(initialSorting);
    const prevInitialStr = prevInitialSortingRef.current;
    
    // Only update if:
    // 1. Initial values changed externally, AND
    // 2. We didn't just update to this value ourselves (prevent circular updates)
    if (initialStr !== prevInitialStr && initialStr !== lastSortingUpdateRef.current) {
      setSorting(initialSorting);
      prevInitialSortingRef.current = initialStr;
    }
  }, [initialSorting]);

  useEffect(() => {
    const initialStr = JSON.stringify(initialColumnFilters);
    const prevInitialStr = prevInitialFiltersRef.current;
    
    // Only update if:
    // 1. Initial values changed externally, AND
    // 2. We didn't just update to this value ourselves (prevent circular updates)
    if (initialStr !== prevInitialStr && initialStr !== lastFiltersUpdateRef.current) {
      setColumnFilters(initialColumnFilters);
      prevInitialFiltersRef.current = initialStr;
    }
  }, [initialColumnFilters]);

  // Notify parent when sorting changes
  useEffect(() => {
    if (onSortingChange) {
      const sortingStr = JSON.stringify(sorting);
      lastSortingUpdateRef.current = sortingStr;
      onSortingChange(sorting);
    }
  }, [sorting, onSortingChange]);

  // Notify parent when column filters change
  useEffect(() => {
    if (onColumnFiltersChange) {
      const filtersStr = JSON.stringify(columnFilters);
      lastFiltersUpdateRef.current = filtersStr;
      onColumnFiltersChange(columnFilters);
    }
  }, [columnFilters, onColumnFiltersChange]);
  const [columnMenuOpen, setColumnMenuOpen] = useState<string | null>(null);
  const [filterInputs, setFilterInputs] = useState<Record<string, string>>({});
  const [addColumnMenuOpen, setAddColumnMenuOpen] = useState(false);
  const [editingCell, setEditingCell] = useState<{ rowId: string; columnId: string } | null>(null);
  const menuRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const [mobileActionSheetOpen, setMobileActionSheetOpen] = useState<string | null>(null); // Contact ID for mobile bottom sheet

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get all available columns (including hidden ones)
  const allColumnKeys = useMemo(() => columns.map(c => c.key), [columns]);
  const hiddenColumns = useMemo(() => {
    return allColumnKeys.filter(key => visibleColumns[key] === false);
  }, [allColumnKeys, visibleColumns]);

  // Custom filter function for prospectStatus that searches both value and label
  const prospectStatusFilterFn = useCallback((row: any, columnId: string, filterValue: string) => {
    const statusValue = row.getValue(columnId) as string | null;
    if (!statusValue) return filterValue === '' || filterValue.toLowerCase().includes('—');
    
    // Search in the raw value (e.g., 'new', 'to_visit')
    const valueMatch = statusValue.toLowerCase().includes(filterValue.toLowerCase());
    
    // Search in the label (e.g., 'Nuovo', 'Da Visitare')
    const option = prospectStatusOptions.find(opt => opt.value === statusValue);
    const labelMatch = option ? option.label.toLowerCase().includes(filterValue.toLowerCase()) : false;
    
    return valueMatch || labelMatch;
  }, [prospectStatusOptions]);

  // Custom filter function for leadStatus that searches both value and label
  const leadStatusFilterFn = useCallback((row: any, columnId: string, filterValue: string) => {
    const contactId = row.original.id;
    const statusValue = leadStages[contactId];
    if (!statusValue) return filterValue === '' || filterValue.toLowerCase().includes('—');
    
    // Search in the raw value (e.g., 'new', 'qualified')
    const valueMatch = statusValue.toLowerCase().includes(filterValue.toLowerCase());
    
    // Search in the label (e.g., 'Nuovi', 'Qualificati')
    const option = leadStatusOptions.find(opt => opt.value === statusValue);
    const labelMatch = option ? option.label.toLowerCase().includes(filterValue.toLowerCase()) : false;
    
    return valueMatch || labelMatch;
  }, [leadStages, leadStatusOptions]);

  // Custom filter function for stage column that handles prospect status
  const stageFilterFn = useCallback((row: any, columnId: string, filterValue: string) => {
    const r = row.original;
    // Determine the current stage: if prospect_status is set, it's a prospect
    const currentStage = r.prospect_status 
      ? 'prospect' 
      : (r.lifecycle_stage || 'contact');
    
    const filterLower = filterValue.toLowerCase();
    
    // Check if filter matches the stage value
    if (currentStage.toLowerCase().includes(filterLower)) {
      return true;
    }
    
    // Check if filter matches the display label
    if (currentStage === 'prospect') {
      if ('prospect'.toLowerCase().includes(filterLower)) return true;
    } else {
      const option = stageOptions.find(opt => opt.value === currentStage);
      if (option && option.label.toLowerCase().includes(filterLower)) {
        return true;
      }
    }
    
    return false;
  }, [stageOptions]);

  // Create column definitions for TanStack Table
  const tableColumns = useMemo<ColumnDef<Contact>[]>(() => {
    const cols: ColumnDef<Contact>[] = [];

    columns.forEach((colDef) => {
      const colKey = colDef.key;
      const isVisible = visibleColumns[colKey] !== false;

      if (!isVisible) return;

      // Map column keys to actual Contact field names
      const accessorKeyMap: Record<string, string> = {
        name: 'full_name',
        company: 'company',
        email: 'email',
        phone: 'phone',
        source: 'source',
        stage: 'lifecycle_stage',
        address: 'company_address',
        city: 'company_city',
        prospectStatus: 'prospect_status',
        leadStatus: 'lead_status', // Virtual field, will be handled in cell renderer
        website: 'website',
        googleMaps: 'google_maps_url',
        tags: 'tags',
        interest_level: 'interest_level',
        vat: 'vat_number',
        rank: 'client_rank',
        lastActivity: 'last_activity_at',
        route_index: 'route_index',
        latest_note: 'latest_note',
      };

      const actualAccessorKey = colDef.accessorKey || accessorKeyMap[colKey] || colKey;
      
      let column: ColumnDef<Contact> = {
        id: colKey,
        accessorKey: colKey === 'leadStatus' ? undefined : actualAccessorKey, // leadStatus uses accessorFn instead
        header: colDef.label,
        enableSorting: colDef.sortable !== false,
        enableColumnFilter: true,
        sortingFn: (rowA, rowB, columnId) => {
          const aValue = rowA.getValue(columnId);
          const bValue = rowB.getValue(columnId);
          
          // Handle null/undefined values
          if (aValue == null && bValue == null) return 0;
          if (aValue == null) return 1;
          if (bValue == null) return -1;
          
          // Handle dates
          if (aValue instanceof Date && bValue instanceof Date) {
            return aValue.getTime() - bValue.getTime();
          }
          
          // Handle strings (including date strings)
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            // Try parsing as dates first
            const dateA = new Date(aValue);
            const dateB = new Date(bValue);
            if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
              return dateA.getTime() - dateB.getTime();
            }
            // Otherwise do string comparison
            return aValue.localeCompare(bValue, undefined, { sensitivity: 'base', numeric: true });
          }
          
          // Handle numbers
          if (typeof aValue === 'number' && typeof bValue === 'number') {
            return aValue - bValue;
          }
          
          // Fallback to string comparison
          return String(aValue).localeCompare(String(bValue), undefined, { sensitivity: 'base', numeric: true });
        },
      };

      // Custom cell renderers and filters
      switch (colKey) {
        case 'stage':
          // Remove accessorKey so accessorFn is used instead
          delete (column as any).accessorKey;
          // Add custom filter function for stage column
          column.filterFn = stageFilterFn;
          // Use accessorFn to return the computed stage value for filtering
          (column as any).accessorFn = (row: Contact) => {
            // Return 'prospect' if prospect_status is set, otherwise return lifecycle_stage
            return row.prospect_status ? 'prospect' : (row.lifecycle_stage || 'contact');
          };
          // Set up the cell renderer
          column.cell = ({ row }) => {
            const r = row.original;
            // Determine the current stage: if prospect_status is set, it's a prospect
            // Otherwise, use lifecycle_stage
            const currentStage = r.prospect_status 
              ? 'prospect' 
              : (r.lifecycle_stage || 'contact');

            // Always show as dropdown if editable, otherwise show as badge
            if (canEdit && !r.readonly) {
              return (
                <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
                  <select
                    value={currentStage}
                    onChange={async (e) => {
                      const newValue = e.target.value;
                      if (onUpdateContact) {
                        // If changing to prospect, set prospect_status to 'new'
                        // Don't change lifecycle_stage - keep it as is
                        if (newValue === 'prospect') {
                          // Set prospect_status to 'new' to mark as prospect
                          // Keep lifecycle_stage unchanged
                          await onUpdateContact(r.id, 'prospect_status', 'new', {});
                        } else {
                          // For contact, lead, or client, clear prospect_status and update lifecycle_stage
                          const additionalFields: Record<string, any> = {};
                          if (r.prospect_status) {
                            additionalFields.prospect_status = null;
                          }
                          await onUpdateContact(r.id, 'lifecycle_stage', newValue as 'contact' | 'lead' | 'client', additionalFields);
                        }
                      }
                    }}
                    className="text-[11px] rounded-full px-2 py-0.5 font-semibold border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-transparent cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <option value="contact">{t('crm.common.stages.contact')}</option>
                    <option value="lead">{t('crm.common.stages.lead')}</option>
                    <option value="client">{t('crm.common.stages.client')}</option>
                    <option value="prospect">Prospect</option>
                  </select>
                </div>
              );
            }

            // Read-only display (badge style)
            let stageLabel: string;
            let stageColor: string;
            if (currentStage === 'prospect') {
              stageLabel = 'Prospect';
              stageColor = 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-700';
            } else if (currentStage === 'client') {
              stageLabel = stageOptions.find((option) => option.value === 'client')?.label || t('crm.common.stages.client');
              stageColor = 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700';
            } else if (currentStage === 'lead') {
              stageLabel = stageOptions.find((option) => option.value === 'lead')?.label || t('crm.common.stages.lead');
              stageColor = 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700';
            } else {
              stageLabel = stageOptions.find((option) => option.value === 'contact')?.label || t('crm.common.stages.contact');
              stageColor = 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600';
            }

            return (
              <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${stageColor}`}
                >
                  {stageLabel}
                </span>
              </div>
            );
          };
          break;
        case 'name':
          column.cell = ({ row }) => {
            const r = row.original;
            const emailDuplicate = r.email ? duplicateEmails.has(r.email.toLowerCase()) : false;
            const openLeads = leadCounts[r.id] || 0;
            return (
              <div
                className={`px-3 py-2 text-gray-700 dark:text-gray-300 ${r.readonly ? 'cursor-default opacity-60' : 'cursor-pointer'}`}
                onClick={() => {
                  if (!r.readonly) navigate(`/crm/contacts/${r.id}`);
                }}
              >
                <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  {r.full_name || '—'}
                  {openLeads > 0 && (
                    <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                      {t('crm.common.openLeadBadge', { count: openLeads })}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-2">
                  {r.email || '—'}
                  {emailDuplicate && (
                    <span className="inline-flex items-center gap-1 text-amber-600">
                      <AlertTriangle className="w-3 h-3" /> {t('crm.contacts.duplicateBadge')}
                    </span>
                  )}
                </div>
              </div>
            );
          };
          break;
        case 'company':
          // Ensure sorting works by using accessorFn explicitly (overrides accessorKey)
          delete (column as any).accessorKey;
          (column as any).accessorFn = (row: Contact) => row.company || '';
          column.cell = ({ row }) => {
            const r = row.original;
            return (
              <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
                <div className="font-medium text-gray-900 dark:text-gray-100">{r.company || '—'}</div>
              </div>
            );
          };
          break;
        case 'email':
          column.cell = ({ row }) => (
            <div className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.original.email || '—'}</div>
          );
          break;
        case 'phone':
          column.cell = ({ row }) => {
            const r = row.original;
            const phoneDuplicate = r.phone ? duplicatePhones.has(r.phone.replace(/\D/g, '')) : false;
            return (
              <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
                <div className="flex items-center gap-2">
                  <span>{r.phone || '—'}</span>
                  {phoneDuplicate && <AlertTriangle className="w-3 h-3 text-amber-600" />}
                </div>
              </div>
            );
          };
          break;
        case 'source':
          column.cell = ({ row }) => (
            <div className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.original.source || '—'}</div>
          );
          break;
        case 'address':
          column.cell = ({ row }) => (
            <div className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.original.company_address || '—'}</div>
          );
          break;
        case 'city':
          column.cell = ({ row }) => (
            <div className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.original.company_city || '—'}</div>
          );
          break;
        case 'prospectStatus':
          // Custom filter function that searches both value and label
          column.filterFn = prospectStatusFilterFn;
          
          column.cell = ({ row }) => {
            const r = row.original;
            const isEditing = editingCell?.rowId === r.id && editingCell?.columnId === 'prospectStatus';
            const statusValue = r.prospect_status;

            if (isEditing && canEdit && !r.readonly) {
              return (
                <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
                  <select
                    autoFocus
                    value={statusValue || ''}
                    onChange={async (e) => {
                      const newValue = e.target.value || null;
                      if (onUpdateContact) {
                        await onUpdateContact(r.id, 'prospect_status', newValue);
                      }
                      setEditingCell(null);
                    }}
                    onBlur={() => setEditingCell(null)}
                    className="text-[11px] rounded-full px-2 py-0.5 font-semibold bg-cyan-100 text-cyan-700 border border-cyan-200"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">—</option>
                    {prospectStatusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }

            return (
              <div
                className="px-3 py-2 text-gray-700 dark:text-gray-300 cursor-pointer"
                onClick={() => {
                  if (canEdit && !r.readonly) {
                    setEditingCell({ rowId: r.id, columnId: 'prospectStatus' });
                  }
                }}
              >
                {r.prospect_status ? (
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-cyan-100 text-cyan-700 border border-cyan-200">
                    {prospectStatusOptions.find((opt) => opt.value === r.prospect_status)?.label || r.prospect_status}
                  </span>
                ) : (
                  '—'
                )}
              </div>
            );
          };
          break;
        case 'leadStatus':
          // Custom filter function that searches both value and label
          column.filterFn = leadStatusFilterFn;
          
          // Use accessorFn to get the lead status from leadStages (virtual field)
          (column as any).accessorFn = (row: Contact) => {
            return leadStages[row.id] || null;
          };
          
          column.cell = ({ row }) => {
            const r = row.original;
            const leadStage = leadStages[r.id];
            
            if (!leadStage) {
              return (
                <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
                  —
                </div>
              );
            }

            const option = leadStatusOptions.find(opt => opt.value === leadStage);
            const label = option?.label || leadStage;

            // Color coding based on lead stage
            let bgColor = 'bg-gray-100';
            let textColor = 'text-gray-700';
            let borderColor = 'border-gray-200';
            
            switch (leadStage) {
              case 'new':
                bgColor = 'bg-blue-100';
                textColor = 'text-blue-700';
                borderColor = 'border-blue-200';
                break;
              case 'qualified':
                bgColor = 'bg-amber-100';
                textColor = 'text-amber-700';
                borderColor = 'border-amber-200';
                break;
              case 'scheduled':
                bgColor = 'bg-cyan-100';
                textColor = 'text-cyan-700';
                borderColor = 'border-cyan-200';
                break;
              case 'won':
                bgColor = 'bg-green-100';
                textColor = 'text-green-700';
                borderColor = 'border-green-200';
                break;
              case 'lost':
                bgColor = 'bg-red-100';
                textColor = 'text-red-700';
                borderColor = 'border-red-200';
                break;
            }

            return (
              <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${bgColor} ${textColor} border ${borderColor}`}>
                  {label}
                </span>
              </div>
            );
          };
          break;
        case 'website':
          column.cell = ({ row }) => {
            const r = row.original;
            return (
              <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
                {r.website ? (
                  <a
                    href={r.website.startsWith('http') ? r.website : `https://${r.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-500 hover:underline"
                  >
                    <Globe className="w-3 h-3" />
                    <span className="text-xs truncate max-w-[120px]">{r.website}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  '—'
                )}
              </div>
            );
          };
          break;
        case 'googleMaps':
          column.cell = ({ row }) => {
            const r = row.original;
            const isInNavigator = r.prospect_status === 'to_visit';
            const hasGoogleMaps = !!r.google_maps_url;
            
            return (
              <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
                <div className="flex items-center gap-2">
                  {hasGoogleMaps && (
                    <a
                      href={r.google_maps_url || ''}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-500 hover:underline"
                    >
                      <MapPin className="w-4 h-4" />
                      <span className="text-xs">Apri</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {canEdit && !r.readonly && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (isInNavigator) {
                          // Already in Navigator, do nothing or show message
                          return;
                        }
                        // Add to Navigator by setting prospect_status to 'to_visit'
                        if (onUpdateContact) {
                          await onUpdateContact(r.id, 'prospect_status', 'to_visit');
                        }
                      }}
                      disabled={isInNavigator}
                      className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${
                        isInNavigator
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 cursor-default'
                          : 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-200 dark:hover:bg-cyan-900/50'
                      }`}
                      title={isInNavigator ? t('crm.contacts.alreadyInNavigator') || 'Già nel Navigatore' : t('crm.contacts.addToNavigator') || 'Aggiungi al Navigatore'}
                    >
                      {isInNavigator ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <Plus className="w-3.5 h-3.5" />
                      )}
                    </button>
                  )}
                  {!hasGoogleMaps && !canEdit && '—'}
                </div>
              </div>
            );
          };
          break;
        case 'tags':
          column.cell = ({ row }) => {
            const tagList = Array.isArray(row.original.tags) ? row.original.tags : [];
            return (
              <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
                <div className="flex flex-wrap gap-1">
                  {tagList.length === 0 ? (
                    <span className="text-xs text-gray-500">{t('crm.contacts.noTag')}</span>
                  ) : (
                    tagList.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-full border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 px-2 py-0.5 text-[11px] text-gray-700 dark:text-gray-300"
                      >
                        <Tags className="w-3 h-3" /> {tag}
                      </span>
                    ))
                  )}
                </div>
              </div>
            );
          };
          break;
        case 'interest_level':
          column.cell = ({ row }) => {
            const r = row.original;
            const isEditing = editingCell?.rowId === r.id && editingCell?.columnId === 'interest_level';
            const interestValue = r.interest_level;

            if (isEditing && canEdit && !r.readonly) {
              return (
                <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
                  <select
                    autoFocus
                    value={interestValue || ''}
                    onChange={async (e) => {
                      const newValue = e.target.value || null;
                      if (onUpdateContact) {
                        await onUpdateContact(r.id, 'interest_level', newValue);
                      }
                      setEditingCell(null);
                    }}
                    onBlur={() => setEditingCell(null)}
                    className="text-[11px] rounded-full px-2 py-0.5 font-semibold border"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">—</option>
                    <option value="low">Basso</option>
                    <option value="medium">Medio</option>
                    <option value="high">Alto</option>
                  </select>
                </div>
              );
            }

            const getInterestColor = (level: string | null | undefined) => {
              switch (level) {
                case 'low':
                  return 'bg-gray-100 text-gray-700 border-gray-200';
                case 'medium':
                  return 'bg-yellow-100 text-yellow-700 border-yellow-200';
                case 'high':
                  return 'bg-green-100 text-green-700 border-green-200';
                default:
                  return '';
              }
            };

            const getInterestLabel = (level: string | null | undefined) => {
              switch (level) {
                case 'low':
                  return 'Basso';
                case 'medium':
                  return 'Medio';
                case 'high':
                  return 'Alto';
                default:
                  return '—';
              }
            };

            return (
              <div
                className="px-3 py-2 text-gray-700 dark:text-gray-300 cursor-pointer"
                onClick={() => {
                  if (canEdit && !r.readonly) {
                    setEditingCell({ rowId: r.id, columnId: 'interest_level' });
                  }
                }}
              >
                {interestValue ? (
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border ${getInterestColor(interestValue)}`}>
                    {getInterestLabel(interestValue)}
                  </span>
                ) : (
                  '—'
                )}
              </div>
            );
          };
          break;
        case 'route_index':
          column.cell = ({ row }) => {
            const r = row.original;
            const routeIndex = r.route_index;
            return (
              <div className="px-3 py-2 text-gray-700 dark:text-gray-300 text-center flex items-center justify-center h-full">
                {routeIndex != null ? (
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 font-semibold text-[10px] border border-cyan-200 dark:border-cyan-700">
                    {routeIndex}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                )}
              </div>
            );
          };
          break;
        case 'vat':
          column.cell = ({ row }) => (
            <div className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.original.vat_number || '—'}</div>
          );
          break;
        case 'rank':
          column.cell = ({ row }) => {
            const r = row.original;
            return (
              <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
                {r.client_rank ? (
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }, (_, idx) => (
                      <Star
                        key={idx}
                        className={`w-3.5 h-3.5 ${idx < (r.client_rank || 0) ? 'text-amber-500' : 'text-gray-300'}`}
                        strokeWidth={1.5}
                        fill={idx < (r.client_rank || 0) ? 'currentColor' : 'none'}
                      />
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-gray-500">{t('crm.contacts.noRank')}</span>
                )}
              </div>
            );
          };
          break;
        case 'lastActivity':
          column.cell = ({ row }) => (
            <div className="px-3 py-2 text-gray-700 dark:text-gray-300 text-sm">
              {formatRelative(row.original.last_activity_at || row.original.created_at)}
            </div>
          );
          break;
        case 'latest_note':
          column.cell = ({ row }) => {
            const note = row.original.latest_note;
            return (
              <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
                <div className="truncate max-w-[200px] text-xs" title={note || ''}>
                  {note || '—'}
                </div>
              </div>
            );
          };
          break;
        default:
          column.cell = ({ row }) => (
            <div className="px-3 py-2 text-gray-700 dark:text-gray-300">
              {String((row.original as any)[colKey] || '—')}
            </div>
          );
      }

      cols.push(column);
    });

    // Actions column (always visible, not sortable)
    cols.push({
      id: 'actions',
      header: t('crm.contacts.columns.actions'),
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div className="sticky right-0 px-3 py-2 text-gray-700 dark:text-gray-300 text-xs bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700/50">
            {canEdit && !r.readonly && (
              <div className="flex justify-end gap-2 whitespace-nowrap">
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (onEditContact) {
                      await onEditContact(r);
                    }
                  }}
                  className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('crm.common.buttons.edit')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onDeleteContact) {
                      onDeleteContact(r);
                    }
                  }}
                  className="px-2 py-1 rounded border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 text-xs hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                >
                  {t('crm.common.buttons.delete')}
                </button>
              </div>
            )}
          </div>
        );
      },
    });

    return cols;
  }, [
    columns,
    visibleColumns,
    duplicateEmails,
    duplicatePhones,
    leadCounts,
    formatRelative,
    stageOptions,
    prospectStatusOptions,
    editingCell,
    canEdit,
    onUpdateContact,
    onEditContact,
    onDeleteContact,
    navigate,
    t,
    prospectStatusFilterFn,
    leadStatusFilterFn,
    stageFilterFn,
    leadStages,
    leadStatusOptions,
  ]);

  // Filter column order to only include visible columns (plus actions)
  const orderedVisibleColumns = useMemo(() => {
    const visible = columnOrder.filter(key => visibleColumns[key] !== false);
    return [...visible, 'actions'];
  }, [columnOrder, visibleColumns]);

  // Reorder table columns based on columnOrder
  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      columnFilters,
      columnOrder: orderedVisibleColumns,
      columnVisibility: visibleColumns,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnOrderChange: (updater) => {
      const newOrder = typeof updater === 'function' ? updater(orderedVisibleColumns) : updater;
      // Remove 'actions' from order update
      const withoutActions = newOrder.filter((id: string) => id !== 'actions');
      onColumnOrderChange(withoutActions);
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility = typeof updater === 'function' ? updater(visibleColumns) : updater;
      onVisibleColumnsChange(newVisibility);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Disable auto-reset page index to prevent infinite loops
    autoResetPageIndex: false,
    filterFns: {
      textFilter: (row, columnId, filterValue: string) => {
        const value = (row.getValue(columnId) as string) || '';
        return value.toLowerCase().includes(filterValue.toLowerCase());
      },
    },
  });

  // Apply column filters - use a ref to track previous filterInputs to avoid infinite loops
  const prevFilterInputsRef = React.useRef<Record<string, string>>({});
  
  useEffect(() => {
    const filterInputsStr = JSON.stringify(filterInputs);
    const prevFilterInputsStr = JSON.stringify(prevFilterInputsRef.current);
    
    // Only update if filterInputs actually changed
    if (filterInputsStr !== prevFilterInputsStr) {
      Object.keys(filterInputs).forEach((columnId) => {
        const column = table.getColumn(columnId);
        if (column) {
          const inputValue = filterInputs[columnId] || '';
          if (inputValue) {
            column.setFilterValue(inputValue);
          } else {
            column.setFilterValue(undefined);
          }
        }
      });
      prevFilterInputsRef.current = { ...filterInputs };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterInputs]);

  // Get filtered and sorted rows (ALL rows, not paginated yet)
  // Use rowModel directly - TanStack Table manages this efficiently
  const rowModel = table.getRowModel();
  const filteredAndSortedRows = rowModel.rows;

  // Notify parent of filtered count - use a ref to prevent infinite loops
  const filteredCountRef = React.useRef(filteredAndSortedRows.length);
  const prevColumnFiltersRef = React.useRef<string>('');
  const prevSortingForCountRef = React.useRef<string>('');
  const prevRowCountRef = React.useRef<number>(filteredAndSortedRows.length);
  
  // Use a separate effect that only runs when filters/sorting change, not on every render
  useEffect(() => {
    const currentFiltersStr = JSON.stringify(columnFilters);
    const currentSortingStr = JSON.stringify(sorting);
    const newCount = filteredAndSortedRows.length;
    
    // Only update if count actually changed or filters/sorting changed
    const filtersChanged = currentFiltersStr !== prevColumnFiltersRef.current;
    const sortingChanged = currentSortingStr !== prevSortingForCountRef.current;
    const countChanged = prevRowCountRef.current !== newCount;
    
    // Only call onFilteredCountChange if something actually changed
    if ((countChanged || filtersChanged || sortingChanged) && onFilteredCountChange) {
      filteredCountRef.current = newCount;
      prevColumnFiltersRef.current = currentFiltersStr;
      prevSortingForCountRef.current = currentSortingStr;
      prevRowCountRef.current = newCount;
      onFilteredCountChange(newCount);
    }
    // Only depend on filters and sorting, not on the row count directly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting]);

  // Use refs to track previous values for proper change detection (for page reset)
  const prevFiltersRef = React.useRef<string>('');
  const prevSortingRef = React.useRef<string>('');
  
  // Reset to page 1 when filters or sorting change (but not when page changes)
  useEffect(() => {
    const filtersStr = JSON.stringify(columnFilters);
    const sortingStr = JSON.stringify(sorting);
    
    // Skip on initial mount (when refs are empty)
    if (prevFiltersRef.current === '' && prevSortingRef.current === '') {
      prevFiltersRef.current = filtersStr;
      prevSortingRef.current = sortingStr;
      return;
    }
    
    const filtersChanged = filtersStr !== prevFiltersRef.current;
    const sortingChanged = sortingStr !== prevSortingRef.current;
    
    // Only reset if filters or sorting actually changed
    if ((filtersChanged || sortingChanged) && onPageChange) {
      onPageChange(1);
    }
    
    // Update refs after checking
    prevFiltersRef.current = filtersStr;
    prevSortingRef.current = sortingStr;
  }, [columnFilters, sorting, onPageChange]); // Removed currentPage from dependencies to prevent loop

  // Apply pagination AFTER filtering and sorting
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedRows.slice(startIndex, endIndex);
  }, [filteredAndSortedRows, currentPage, itemsPerPage]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeIndex = orderedVisibleColumns.indexOf(active.id as string);
    const overIndex = orderedVisibleColumns.indexOf(over.id as string);

    if (activeIndex === -1 || overIndex === -1) return;

    const newOrder = arrayMove(orderedVisibleColumns, activeIndex, overIndex);
    // Remove 'actions' from reordering
    const withoutActions = newOrder.filter((id: string) => id !== 'actions');
    // Ensure 'actions' stays at the end
    onColumnOrderChange(withoutActions);
  };

  const toggleColumnVisibility = (columnKey: string) => {
    onVisibleColumnsChange({
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey],
    });
    setColumnMenuOpen(null);
  };

  const addColumn = (columnKey: string) => {
    onVisibleColumnsChange({
      ...visibleColumns,
      [columnKey]: true,
    });
    setAddColumnMenuOpen(false);
  };

  const handleFilterChange = (columnId: string, value: string) => {
    setFilterInputs((prev) => ({
      ...prev,
      [columnId]: value,
    }));
    const column = table.getColumn(columnId);
    if (column) {
      if (value) {
        column.setFilterValue(value);
      } else {
        column.setFilterValue(undefined);
      }
    }
  };

  const clearFilter = (columnId: string) => {
    setFilterInputs((prev) => {
      const newInputs = { ...prev };
      delete newInputs[columnId];
      return newInputs;
    });
    const column = table.getColumn(columnId);
    if (column) {
      column.setFilterValue(undefined);
    }
  };

  const clearAllFilters = () => {
    // Clear all filter inputs
    setFilterInputs({});
    
    // Clear all column filters in the table
    table.getAllColumns().forEach((column) => {
      if (column.id !== 'actions') {
        column.setFilterValue(undefined);
      }
    });
  };

  // Check if there are any active filters
  const hasActiveFilters = Object.keys(filterInputs).length > 0 || 
    table.getState().columnFilters.length > 0;

  return (
    <div className="relative w-full max-w-full overflow-hidden" style={{ maxWidth: '100%', width: '100%', boxSizing: 'border-box' }}>
      {/* Clear All Filters Button */}
      {hasActiveFilters && (
        <div className="mb-2 flex justify-end">
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors border border-gray-300 dark:border-gray-600"
          >
            <X className="w-4 h-4" />
            <span>Rimuovi tutti i filtri</span>
          </button>
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        {/* Desktop Table - Hidden on Mobile */}
        <div className="hidden md:block relative overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-md w-full" style={{ maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', boxSizing: 'border-box' }}>
          <table className="w-full text-left" style={{ minWidth: 'max-content' }}>
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <SortableContext
                  items={orderedVisibleColumns.filter(id => id !== 'actions')}
                  strategy={horizontalListSortingStrategy}
                >
                  {table.getHeaderGroups().map((headerGroup) =>
                    headerGroup.headers
                      .filter((header) => header.id !== 'actions')
                      .map((header) => {
                        const columnDef = columns.find((c) => c.key === header.id);
                        if (!columnDef) return null;

                        return (
                          <SortableHeader
                            key={header.id}
                            column={header.column}
                            menuOpen={columnMenuOpen === header.id}
                            menuRef={React.createRef<HTMLDivElement>()}
                            onMenuClick={(e) => {
                              e.stopPropagation();
                              const menuElement = (e.currentTarget.parentElement as HTMLDivElement);
                              menuRefs.current[header.id] = menuElement;
                              setColumnMenuOpen(columnMenuOpen === header.id ? null : header.id);
                            }}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </SortableHeader>
                        );
                      })
                  )}
                </SortableContext>
                {/* Add Column Button */}
                <th className="px-3 py-2 text-gray-700 dark:text-gray-300 font-semibold text-xs uppercase tracking-wider bg-gray-50 dark:bg-gray-700/50 sticky top-0 z-10">
                  <div className="relative">
                    <button
                      onClick={() => setAddColumnMenuOpen(!addColumnMenuOpen)}
                      className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="Aggiungi colonna"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    {addColumnMenuOpen && hiddenColumns.length > 0 && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setAddColumnMenuOpen(false)}
                        />
                        <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 min-w-[200px] max-h-[300px] overflow-y-auto">
                          <div className="p-2">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-2 py-1 mb-1">
                              Colonne nascoste
                            </div>
                            {hiddenColumns.map((key) => {
                              const colDef = columns.find((c) => c.key === key);
                              return (
                                <button
                                  key={key}
                                  onClick={() => addColumn(key)}
                                  className="w-full text-left px-2 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                >
                                  {colDef?.label || key}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </th>
                {/* Actions Header */}
                <th className="sticky right-0 px-3 py-2 text-gray-700 dark:text-gray-300 font-semibold text-xs uppercase tracking-wider text-right bg-gray-50 dark:bg-gray-700/50 sticky top-0 z-10">
                  {(() => {
                    const actionsHeader = table.getHeaderGroups()[0].headers.find((h) => h.id === 'actions');
                    if (actionsHeader) {
                      return flexRender(
                        actionsHeader.column.columnDef.header,
                        actionsHeader.getContext()
                      );
                    }
                    return null;
                  })()}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row) => (
                <tr
                  key={row.id}
                  className="group border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  {row.getVisibleCells()
                    .filter((cell) => cell.column.id !== 'actions')
                    .map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  {row.getVisibleCells()
                    .filter((cell) => cell.column.id === 'actions')
                    .map((cell) => (
                      <td key={cell.id} className="sticky right-0 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700/50">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card List - Only on Mobile */}
        <div className="block md:hidden space-y-3 pb-20">
          {paginatedRows.map((row) => {
            const contact = row.original;
            const companyName = contact.company || contact.full_name || '—';
            const city = contact.company_city || '';
            
            // Get status info
            const prospectStatus = contact.prospect_status;
            const lifecycleStage = contact.lifecycle_stage;
            const stageStatus = prospectStatus ? 'prospect' : lifecycleStage || 'contact';
            
            // Status color and label
            let statusColor = 'bg-gray-400';
            let statusLabel = 'Contatto';
            if (stageStatus === 'prospect') {
              statusColor = 'bg-purple-500';
              statusLabel = 'Prospect';
            } else if (stageStatus === 'client') {
              statusColor = 'bg-emerald-500';
              statusLabel = stageOptions.find(opt => opt.value === 'client')?.label || t('crm.common.stages.client');
            } else if (stageStatus === 'lead') {
              statusColor = 'bg-amber-500';
              statusLabel = stageOptions.find(opt => opt.value === 'lead')?.label || t('crm.common.stages.lead');
            }
            
            return (
              <div
                key={contact.id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm active:scale-[0.98] transition-transform"
                onClick={() => {
                  if (!contact.readonly) {
                    navigate(`/crm/contacts/${contact.id}`);
                  }
                }}
              >
                {/* Header: Company Name (Bold, Large) */}
                <h3 className={`text-xl font-bold text-gray-900 dark:text-white mb-1 truncate ${!contact.readonly ? 'cursor-pointer' : 'cursor-default'}`}>
                  {companyName}
                </h3>
                
                {/* Subheader: City (Gray, Small) */}
                {city && (
                  <p className="text-sm text-gray-400 dark:text-gray-500 mb-3 truncate">
                    {city}
                  </p>
                )}
                
                {/* Body: Phone, Status, Website, Route Index */}
                <div className="flex flex-col gap-2 mb-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    {contact.phone && (
                      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                        <span className="text-lg">📞</span>
                        <span className="text-sm">{contact.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${statusColor}`} />
                      <span className="text-xs text-gray-600 dark:text-gray-400">{statusLabel}</span>
                    </div>

                    {contact.interest_level && (
                      <div className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                        contact.interest_level === 'high' ? 'bg-green-100 text-green-700 border-green-200' :
                        contact.interest_level === 'medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                        'bg-gray-100 text-gray-700 border-gray-200'
                      }`}>
                        {contact.interest_level === 'high' ? 'Alto' :
                         contact.interest_level === 'medium' ? 'Medio' : 'Basso'}
                      </div>
                    )}
                  </div>

                  {/* Website and Route Index Row */}
                  {(contact.website || contact.route_index != null || contact.latest_note) && (
                    <div className="pt-2 mt-1 border-t border-gray-100 dark:border-gray-700/50 space-y-2">
                      {/* Note Preview */}
                      {contact.latest_note && (
                        <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400 italic bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded-lg border border-yellow-100 dark:border-yellow-900/30">
                          <span className="font-medium not-italic text-yellow-700 dark:text-yellow-500">Note:</span>
                          <span className="line-clamp-2">{contact.latest_note}</span>
                        </div>
                      )}

                      <div className="flex items-center justify-between gap-3">
                        {contact.website ? (
                          <a
                            href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1.5 text-cyan-600 dark:text-cyan-400 hover:underline truncate min-w-0"
                          >
                            <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="text-xs truncate max-w-[180px]">{contact.website}</span>
                          </a>
                        ) : <div></div>}
                        
                        {contact.route_index != null && (
                          <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium">Idx</span>
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 font-bold text-xs border border-cyan-200 dark:border-cyan-700">
                              {contact.route_index}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Actions: More Options (Three Dots) - Opens Bottom Sheet */}
                {canEdit && !contact.readonly && (
                  <div className="flex justify-end">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setMobileActionSheetOpen(contact.id);
                      }}
                      className="p-3 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 active:scale-95 transition-transform min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
                      title="More Options"
                    >
                      <MoreHorizontal className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Mobile: Action Bottom Sheet */}
        {mobileActionSheetOpen && (() => {
          const contact = paginatedRows.find(row => row.original.id === mobileActionSheetOpen)?.original;
          if (!contact) return null;

          return (
            <div className="md:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={() => setMobileActionSheetOpen(null)}>
              <div 
                className="fixed inset-x-0 bottom-0 bg-white dark:bg-gray-800 rounded-t-xl shadow-2xl border-t border-gray-200 dark:border-gray-700 transition-transform duration-300 ease-out translate-y-0"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Drag Handle */}
                <div 
                  onClick={() => setMobileActionSheetOpen(null)}
                  className="flex items-center justify-center pt-3 pb-2 cursor-pointer touch-none"
                >
                  <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
                </div>

                {/* Contact Info */}
                <div className="px-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                    {contact.company || contact.full_name || '—'}
                  </h3>
                  {contact.company_city && (
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      {contact.company_city}
                    </p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="p-4 space-y-2">
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      setMobileActionSheetOpen(null);
                      if (onEditContact) {
                        await onEditContact(contact);
                      }
                    }}
                    className="w-full px-4 py-4 rounded-xl bg-cyan-500 dark:bg-cyan-600 text-white font-semibold text-base hover:bg-cyan-600 dark:hover:bg-cyan-700 active:scale-[0.98] transition-transform shadow-md min-h-[56px] flex items-center justify-center gap-2"
                  >
                    <Edit2 className="w-5 h-5" />
                    Modifica
                  </button>
                  {onDeleteContact && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMobileActionSheetOpen(null);
                        onDeleteContact(contact);
                      }}
                      className="w-full px-4 py-4 rounded-xl bg-red-600 dark:bg-red-500 text-white font-semibold text-base hover:bg-red-700 dark:hover:bg-red-600 active:scale-[0.98] transition-transform shadow-md min-h-[56px] flex items-center justify-center gap-2"
                    >
                      <X className="w-5 h-5" />
                      Elimina
                    </button>
                  )}
                  <button
                    onClick={() => {
                      navigate(`/crm/contacts/${contact.id}`);
                      setMobileActionSheetOpen(null);
                    }}
                    className="w-full px-4 py-4 rounded-xl border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold text-base hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-[0.98] transition-transform min-h-[56px] flex items-center justify-center gap-2"
                  >
                    Visualizza Dettagli
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </DndContext>

      {/* Column Menu Popovers */}
      {table.getHeaderGroups().map((headerGroup) =>
        headerGroup.headers
          .filter((header) => header.id !== 'actions' && columnMenuOpen === header.id)
          .map((header) => {
            const columnKey = header.id;
            const filterValue = filterInputs[columnKey] || '';
            const hasFilter = filterValue.length > 0;
            const menuElement = menuRefs.current[columnKey];
            const rect = menuElement?.getBoundingClientRect();

            return (
              <React.Fragment key={header.id}>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setColumnMenuOpen(null)}
                />
                <div
                  className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 z-50 min-w-[250px]"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    top: rect ? `${rect.bottom + 4}px` : '50px',
                    left: rect ? `${rect.left}px` : '50%',
                    transform: rect ? 'none' : 'translateX(-50%)',
                  }}
                >
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Filtra {columns.find((c) => c.key === columnKey)?.label}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={filterValue}
                          onChange={(e) => handleFilterChange(columnKey, e.target.value)}
                          placeholder="Cerca..."
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                          autoFocus
                        />
                        {hasFilter && (
                          <button
                            onClick={() => clearFilter(columnKey)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => toggleColumnVisibility(columnKey)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      <EyeOff className="w-4 h-4" />
                      <span>Nascondi colonna</span>
                    </button>
                  </div>
                </div>
              </React.Fragment>
            );
          })
      )}
    </div>
  );
}

